"use client";
import { themePackStorage } from "@utils/onboarding/themePackStorage";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatTabComp/ChatTabHeader";
import { Icon } from "@ospweb/sdk-ui";
import { cn, pubsub } from "utils";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { ROUTER_PATH } from "@config/router";
import { rootStore } from "@/src/store/rootStore";
import { themePackSessionKeys } from "@/src/utils/onboarding/themePackSessionKeys";

import { ChatTabMoreMenu, type ChatTabMoreMenuItemId } from "./ChatTabMoreMenu";
import type { ChatTabMoreMenuItemUiStateMap } from "@utils/chatTabMoreMenuItemUiState";
import { MenuModal } from "@components/Menu/MenuModal";
import {
  useMainTabHeaderLogic,
  RENEW_THRESHOLD_MINUTES,
} from "@/src/hooks/useMainTabHeaderLogic";

const I18n = I18nUtil.createScoped(Translations);

export { RENEW_THRESHOLD_MINUTES };

export type ChatTabHeaderProps = {
  className?: string;
  onBellClick?: () => void;
  onMoreMenuItemClick?: (itemId: ChatTabMoreMenuItemId) => void;
  moreMenuItemUi?: ChatTabMoreMenuItemUiStateMap;
};

export const ChatTabHeader = ({
  className,
  onMoreMenuItemClick,
  moreMenuItemUi,
}: ChatTabHeaderProps) => {
  const router = useShellRouter();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id;
  const [packExitOwnerId, setPackExitOwnerId] = useState<string | null>(null);
  const packExit = Boolean(ownerId && packExitOwnerId === ownerId);
  useEffect(() => {
    const refresh = () =>
      setPackExitOwnerId(
        ownerId &&
          themePackStorage.getItem(
            themePackSessionKeys.chatExitBrief(ownerId),
          ) === `chat:${localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID)}`
          ? ownerId
          : null,
      );
    refresh();
    return pubsub.on(pubSubKeyMap.THEME_PACK_CHAT_CONTEXT_CHANGED, refresh);
  }, [ownerId]);
  const { labelConfig, isMenuOpen, openMenu, closeMenu, commonClickHandler } =
    useMainTabHeaderLogic("edgen_chat_memu_upgrade");

  return (
    <>
      <div
        className={cn(
          "h-14 px-4 flex items-center justify-between w-full",
          className,
        )}
        data-testid="chat-tab-header"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center size-6 md:hidden"
            onClick={() => {
              openMenu();
            }}
            aria-label="Menu"
          >
            <Icon name="menu" size={24} className="text-content_primary" />
          </button>
          <div className="headline_regular_s text-content_primary md:block hidden">
            {I18n.t("Ask Ed")}
          </div>

          {labelConfig && (
            <button
              type="button"
              className={`flex items-center gap-[2px] rounded-[46px] px-2 py-[5px] body_bold_m ${labelConfig.className}`}
              onClick={() => commonClickHandler(labelConfig.onClick)}
              aria-label={labelConfig.text}
            >
              <Icon name="aura" size={16} />
              {labelConfig.text}
            </button>
          )}
        </div>
        <div className="flex items-center gap-4 relative">
          {packExit ? (
            <button
              type="button"
              aria-label={I18n.t("Close Chat to Brief")}
              data-openreplay-label="pack_chat_close_brief"
              onClick={() => {
                if (ownerId) {
                  themePackStorage.removeItem(
                    themePackSessionKeys.chatExitBrief(ownerId),
                  );
                }
                setPackExitOwnerId(null);
                router.replace(ROUTER_PATH.BRIEF);
              }}
              className="flex size-10 items-center justify-center rounded-full bg-background_solid_neutral_primary"
            >
              <Icon name="close" size={24} />
            </button>
          ) : null}
          <ChatTabMoreMenu
            itemUi={moreMenuItemUi}
            onItemClick={onMoreMenuItemClick}
          />
        </div>
      </div>
      <MenuModal isOpen={isMenuOpen} onCancel={closeMenu} placement="left" />
    </>
  );
};
